import { Group, TextInput, Button, Text } from '@mantine/core';
import { Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { QuickFilterConfig, FilterFieldConfig, ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { SortState } from '../../../utils/sortTasks';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { DataLibraryAttendanceFilterOptions } from '../../../types';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

interface AttendanceToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  filterOptions: DataLibraryAttendanceFilterOptions;
  activeQuickFilters: string[];
  onQuickFilterToggle: (key: string) => void;
  activeFilters: ActiveFilterRow[];
  onFilterAdd: (key: string) => void;
  onFilterChange: (key: string, values: string[]) => void;
  onFilterRemove: (key: string) => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  sortConfig: SortConfig | null;
  onSortChange: (sort: SortState | null) => void;
  activeGroup: GroupState | null;
  /** Filtered row total when it is too large to group */
  groupLimitTotal?: number | null;
  onGroupChange: (group: GroupState | null) => void;
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  totalCount: number;
  selectedCohortIds: string[];
}

const QUICK_FILTERS: QuickFilterConfig[] = [
  { key: 'absent', label: 'Absent Only' },
  { key: 'unexcused', label: 'Unexcused Only' },
];

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'participant_full_name', label: 'Participant Full Name' },
  { value: 'participant_email', label: 'Participant Email' },
  { value: 'session_number', label: 'Session Number' },
  { value: 'session_date', label: 'Session Date' },
  { value: 'group', label: 'Group' },
  { value: 'coach', label: 'Coach' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'overall_attendance_rate', label: 'Overall Attendance Rate' },
  { value: 'session_attendance_rate', label: 'Session Attendance Rate' },
  { value: 'avg_engagement', label: 'Avg Engagement' },
  { value: 'total_unexcused', label: 'Total # Unexcused' },
  { value: 'total_missed', label: 'Total # Missed' },
];


const ATTENDANCE_STATUS_OPTIONS = [
  { value: 'attended', label: 'Attended' },
  { value: 'partial', label: 'Partially Attended' },
  { value: 'excused_business', label: 'Excused — Business' },
  { value: 'excused_personal', label: 'Excused — Personal' },
  { value: 'unexcused', label: 'Unexcused' },
  { value: 'not_marked', label: 'Not Marked' },
];

export function AttendanceToolbar({
  searchValue,
  onSearchChange,
  filterOptions,
  activeQuickFilters,
  onQuickFilterToggle,
  activeFilters,
  onFilterAdd,
  onFilterChange,
  onFilterRemove,
  onClearAll,
  hasActiveFilters,
  sortConfig,
  onSortChange,
  activeGroup,
  groupLimitTotal,
  onGroupChange,
  columns,
  onColumnsChange,
  totalCount,
  selectedCohortIds,
}: AttendanceToolbarProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);

  const filterFields: FilterFieldConfig[] = useMemo(
    () => [
      {
        key: 'cohort',
        label: 'Cohort',
        type: 'select' as const,
        options: [], // Cohort filtering is handled by the side panel
      },
      {
        key: 'group',
        label: 'Group',
        type: 'multiselect' as const,
        options: filterOptions.groups,
      },
      {
        key: 'coach',
        label: 'Coach',
        type: 'select' as const,
        options: filterOptions.coaches,
      },
      {
        key: 'session_number',
        label: 'Session Number',
        type: 'select' as const,
        options: filterOptions.sessionNumbers,
      },
      {
        key: 'attendance',
        label: 'Attendance',
        type: 'select' as const,
        options: ATTENDANCE_STATUS_OPTIONS,
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select' as const,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'withdrawn', label: 'Withdrawn' },
        ],
      },
      {
        key: 'department',
        label: 'Department',
        type: 'select' as const,
        options: filterOptions.departments.map(d => ({ value: d, label: d })),
      },
      {
        key: 'location',
        label: 'Location',
        type: 'select' as const,
        options: filterOptions.locations.map(l => ({ value: l, label: l })),
      },
      {
        key: 'business_unit',
        label: 'Business Unit',
        type: 'select' as const,
        options: filterOptions.businessUnits.map(b => ({ value: b, label: b })),
      },
    ],
    [filterOptions],
  );

  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  // Build the export filter context so the export matches the on-screen view.
  // Mirrors useDataLibraryAttendance.buildParams (minus pagination/sort) — the
  // export worker's fetchAttendance reads these same keys (MED-1233).
  const exportFilterContext = useMemo(() => {
    const ctx: Record<string, unknown> = {};
    if (searchValue) ctx.search = searchValue;
    if (activeQuickFilters.includes('absent')) ctx.absentOnly = true;
    if (activeQuickFilters.includes('unexcused')) ctx.unexcusedOnly = true;
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'status':
          ctx.status = filter.values[0];
          break;
        case 'group':
          ctx.groupIds = filter.values;
          break;
        case 'coach':
          ctx.coachId = filter.values[0];
          break;
        case 'session_number':
          ctx.sessionNumber = filter.values.join(',');
          break;
        case 'attendance':
          ctx.attendanceStatus = filter.values.join(',');
          break;
        case 'department':
          ctx.department = filter.values[0];
          break;
        case 'location':
          ctx.location = filter.values[0];
          break;
        case 'business_unit':
          ctx.businessUnit = filter.values[0];
          break;
      }
    }
    return ctx;
  }, [searchValue, activeQuickFilters, activeFilters]);

  // MDF-208: the keys that exportFilterContext above sends.
  const exportFilterSplit = splitScreenFilters(
    { search: searchValue, quickFilters: QUICK_FILTERS, activeQuickFilters, fields: filterFields, rows: activeFilters },
    ['search', 'absent', 'unexcused', 'status', 'group', 'coach', 'session_number', 'attendance', 'department', 'location', 'business_unit'],
  );

  return (
    <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
      <Group gap="xs" wrap="wrap">
        <TextInput
          placeholder="Search attendance..."
          size="sm"
          leftSection={<Search size={14} />}
          rightSection={
            searchValue ? (
              <X
                size={14}
                style={{ cursor: 'pointer' }}
                onClick={() => onSearchChange('')}
              />
            ) : null
          }
          value={searchValue}
          onChange={e => onSearchChange(e.currentTarget.value)}
          w={220}
        />

        <FilterPanel
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          onClearAll={onClearAll}
          size="sm"
        />

        <SortPanel
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          defaultSort={{ field: 'session_number', direction: 'desc' }}
          onSortChange={onSortChange}
          size="sm"
        />

        <GroupPanel
          groupFields={groupFields}
          activeGroup={activeGroup}
          limitTotal={groupLimitTotal}
          onGroupChange={onGroupChange}
          size="sm"
        />

        <SavedViewsMenu
          columns={columns}
          onColumnsChange={onColumnsChange}
          filterFields={filterFields}
          activeFilters={activeFilters}
          onFilterAdd={onFilterAdd}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
          quickFilters={QUICK_FILTERS}
          activeQuickFilters={activeQuickFilters}
          onQuickFilterToggle={onQuickFilterToggle}
          sortFields={SORT_FIELDS}
          activeSort={activeSortState}
          onSortChange={onSortChange}
          groupFields={groupFields}
          activeGroup={activeGroup}
          onGroupChange={onGroupChange}
          size="sm"
        />

        {hasActiveFilters && (
          <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(onClearAll)}>
            Clear
          </Button>
        )}
      </Group>

      <Group gap="xs">
        <Text size="sm" c="dimmed">
          {totalCount} record{totalCount !== 1 ? 's' : ''}
        </Text>
        <ColumnSettingsPopover columns={columns} onColumnsChange={onColumnsChange} zIndex={1000} />
        <Button
          variant="outline"
          color="gray"
          bg="white"
          size="sm"
          style={{ borderColor: '#E8E6E3' }}
          leftSection={<Download size={14} />}
          onClick={() => setExportOpen(true)}
        >
          Export
        </Button>
      </Group>

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="attendance"
        contextType="global"
        scopeContext={{ cohortIds: selectedCohortIds }}
        filterContext={exportFilterContext}
        {...exportSummaryProps(exportFilterSplit)}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
      />
    </Group>
  );
}
